import React from 'react'
import {Formik} from 'formik'
import {toast} from 'react-toastify'
import pick from 'lodash/pick'
import * as yup from 'yup'

import {
  Typography,
  Grid,
  FormControlLabel,
  RadioGroup,
  Radio,
  Switch,
} from '@material-ui/core'

import {REGEX_NUMBER_ONLY} from '../../../../../../constant'
import ErrorMessage from '../../../../../../components/ErrorMessage'
import {VAT} from '../../../../../../constants'
import {
  GRID_CONTAINER_SPACING,
  GridItem,
  SectorContainer,
  SaveButton,
  FormikTextField,
} from './common'

const hasTaxInformation = shopDetail => {
  const taxInformation = pick(shopDetail, [
    'address',
    'subdistrict',
    'district',
    'province',
    'postcode',
    'phoneNumber',
  ])
  return Object.keys(taxInformation).every(key => !!taxInformation[key])
}

const validationSchema = shopDetail =>
  yup.object({
    vatOptions: yup
      .string()
      .oneOf([VAT.OPTIONS.INCLUDED, VAT.OPTIONS.EXCLUDED, VAT.OPTIONS.NONE]),
    vatPercentage: yup.number().required(),
    vatId: yup.string().when('vatOptions', {
      is: option =>
        option === VAT.OPTIONS.INCLUDED || option === VAT.OPTIONS.EXCLUDED,
      then: yup
        .string()
        .length(13, 'กรุณาใส่หมายเลขประจำตัวผู้เสียภาษี 13 หลัก')
        .matches(REGEX_NUMBER_ONLY, {
          message: 'กรุณาใส่หมายเลขประจำตัวผู้เสียภาษี 13 หลัก',
        })
        .required('กรุณาใส่หมายเลขประจำตัวผู้เสียภาษี 13 หลัก'),
    }),
    isShowTaxInfo: hasTaxInformation(shopDetail)
      ? yup.boolean().required()
      : yup
          .boolean()
          .oneOf(
            [false],
            'กรุณากรอกข้อมูลที่อยู่และเบอร์โทรศัพท์ ก่อนเปลี่ยนแปลงข้อมูลนี้',
          ),
  })

class VATSector extends React.Component {
  render() {
    return (
      <SectorContainer>
        <Formik
          enableReinitialize
          initialValues={{
            ...this.props.value,
          }}
          onSubmit={(values, {setSubmitting}) => {
            this.props
              .onSave({
                variables: {
                  projectId: this.props.project.id,
                  vatId: this.props.value.id,
                  vat: {
                    option: values.vatOptions,
                    rate: values.vatPercentage,
                    taxId: values.vatId,
                    isShowTaxInfo: values.isShowTaxInfo,
                  },
                },
              })
              .then(() => {
                setSubmitting(false)
                toast.success(
                  'บันทึกข้อมูลการตั้งค่าการคำนวณภาษีมูลค่าเพิ่ม (VAT) เรียบร้อยแล้ว',
                  {
                    hideProgressBar: true,
                    position: toast.POSITION.BOTTOM_RIGHT,
                  },
                )
              })
          }}
          validationSchema={() => validationSchema(this.props.shopDetail)}
          render={props => {
            const {
              isSubmitting,
              handleSubmit,
              handleChange,
              handleBlur,
              touched,
              errors,
              values,
            } = props

            return (
              <form onSubmit={handleSubmit}>
                <Typography variant="headline" gutterBottom>
                  ตั้งค่าการคำนวณภาษีมูลค่าเพิ่ม (VAT)
                </Typography>
                <Grid container spacing={GRID_CONTAINER_SPACING}>
                  <GridItem item xs={12} sm={12}>
                    <RadioGroup
                      name="vatOptions"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.vatOptions}
                    >
                      <FormControlLabel
                        key="none"
                        disabled={this.props.disabled}
                        value={VAT.OPTIONS.NONE}
                        control={<Radio />}
                        label="ไม่คิดค่า VAT กับ ลูกค้า"
                      />
                      <FormControlLabel
                        key="included"
                        disabled={this.props.disabled}
                        value={VAT.OPTIONS.INCLUDED}
                        control={<Radio />}
                        label="คิดค่า VAT กับลูกค้า: ราคาที่สินค้ากำหนด ได้รวมกับภาษีมูลค่าเพิ่มแล้ว"
                      />
                      <FormControlLabel
                        key="excluded"
                        disabled={this.props.disabled}
                        value={VAT.OPTIONS.EXCLUDED}
                        control={<Radio />}
                        label="คิดค่า VAT กับลูกค้า: ราคาที่สินค้ากำหนด ยังไม่รวมกับภาษีมูลค่าเพิ่ม"
                      />
                    </RadioGroup>
                  </GridItem>
                </Grid>
                <Grid container spacing={GRID_CONTAINER_SPACING}>
                  <GridItem item xs={12} sm="auto">
                    <Typography align="center" variant="body1">
                      กำหนดเปอร์เซ็นต์ภาษีมูลค่าเพิ่ม (%VAT)
                    </Typography>
                  </GridItem>
                  <GridItem item xs={12} sm={2}>
                    <FormikTextField
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                      disabled
                      name="vatPercentage"
                      value={values.vatPercentage}
                      label=""
                      fullWidth
                      endAdornment="%"
                    />
                  </GridItem>
                </Grid>
                <Grid container spacing={GRID_CONTAINER_SPACING}>
                  <GridItem item xs={12} sm="auto">
                    <Typography variant="body1">
                      หมายเลขประจำตัวผู้เสียภาษี 13 หลัก
                    </Typography>
                  </GridItem>
                  <GridItem item xs={12} sm={3}>
                    <FormikTextField
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched}
                      errors={errors}
                      disabled={this.props.disabled}
                      name="vatId"
                      value={values.vatId}
                      label=""
                      fullWidth
                    />
                  </GridItem>
                </Grid>
                <Grid container spacing={GRID_CONTAINER_SPACING}>
                  <GridItem item xs={12} sm="auto">
                    <Typography variant="body1">
                      แสดงใบกำกับภาษีอย่างย่อ
                    </Typography>
                  </GridItem>

                  <GridItem item xs={12} sm={3}>
                    <Switch
                      name="isShowTaxInfo"
                      checked={values.isShowTaxInfo}
                      onChange={handleChange}
                    />
                    <ErrorMessage>{errors.isShowTaxInfo}</ErrorMessage>
                  </GridItem>
                </Grid>
                <Grid
                  container
                  direction="row-reverse"
                  spacing={GRID_CONTAINER_SPACING}
                >
                  <GridItem item xs={12} sm={3}>
                    <SaveButton
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting || this.props.disabled}
                      fullWidth
                    >
                      Save
                    </SaveButton>
                  </GridItem>
                </Grid>
              </form>
            )
          }}
        />
      </SectorContainer>
    )
  }
}

export default VATSector



// WEBPACK FOOTER //
// ./src/main/content/project/ecommerce/storeSettings/components/VATSector.js